import React, { useEffect, useState } from 'react'
import { Row, Col, InputNumber, Radio, Space, Button as AntdButton, message, Typography, Form } from 'antd'
import { useDispatch } from 'react-redux'
import { getConfigByKey, updateConfigByKey } from 'services/misc.service'
import Modal from 'components/Modal/Modal'
import Spinner from 'components/Spinner/Spinner'
import { FormInputNumber } from 'components/FormItems/FlatFormItems'
import Switch from 'components/Switch/Switch.index'
import { Button } from 'components/Buttons/Buttons'
import { setLoading } from 'redux/actions/app.actions'
import { openNotificationWithIcon } from 'utils/Notification'

const { Text } = Typography

export default function TfaSettingsPopup({ visible, onClose }) {
    const dispatch = useDispatch()

    // keys
    const KEY_EXPIRY = 'Totp:ExpiryInMinutes'
    const KEY_ENABLED = 'IsTwoFactorEnabledGlobally'
    const KEY_PREFERENCE = 'GlobalTwoFactorPreference'

    // state for values
    const [expiry, setExpiry] = useState(null) // number
    const [enabled, setEnabled] = useState(false) // boolean
    const [preference, setPreference] = useState(null) // Email = email, Phone = phone

    // loading flags
    const [loadingExpiry, setLoadingExpiry] = useState(false)
    const [loadingEnabled, setLoadingEnabled] = useState(false)
    const [loadingPref, setLoadingPref] = useState(false)

    const [savingExpiry, setSavingExpiry] = useState(false)
    const [savingEnabled, setSavingEnabled] = useState(false)
    const [savingPref, setSavingPref] = useState(false)

    // fetch individual configs
    const loadExpiry = async () => {
        setLoadingExpiry(true)
        try {
            const res = await dispatch(getConfigByKey(KEY_EXPIRY, false))
            if (res?.status === 'success') {
                // backend stores value as string; parse to number
                const val = res.data?.value
                const n = val !== undefined && val !== null ? Number(val) : null
                setExpiry(Number.isFinite(n) ? n : null)
            }
        } finally {
            setLoadingExpiry(false)
        }
    }

    const loadEnabled = async () => {
        setLoadingEnabled(true)
        try {
            const res = await dispatch(getConfigByKey(KEY_ENABLED, false))
            if (res?.status === 'success') {
                const val = res.data?.value
                // backend may store "true"/"false" or "1"/"0"
                const bool = val === 'true' || val === true || val === '1' || val === 1
                setEnabled(Boolean(bool))
            }
        } finally {
            setLoadingEnabled(false)
        }
    }

    const loadPref = async () => {
        setLoadingPref(true)
        try {
            const res = await dispatch(getConfigByKey(KEY_PREFERENCE, false))
            if (res?.status === 'success') {
                const val = res.data?.value
                setPreference(val ? val : null)
            }
        } finally {
            setLoadingPref(false)
        }
    }

    useEffect(() => {
        if (visible) {
            // load each config independently
            loadExpiry()
            loadEnabled()
            loadPref()
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [visible])

    // save handlers
    const saveExpiry = async (value) => {
        setSavingExpiry(true)
        let result = null
        try {
            const res = await dispatch(updateConfigByKey(KEY_EXPIRY, value, false))
            if (res?.status === 'success') {
                // message.success('OTP expiry updated')
                setExpiry(value)
                result = { success: true }
            } else {
                message.error('Failed to update setting "OTP Expiry Time"')
            }
        } catch (e) {
            message.error(e?.message || 'Failed to update setting "OTP Expiry Time"')
        } finally {
            setSavingExpiry(false)
        }
        return result
    }

    const saveEnabled = async (value) => {
        setSavingEnabled(true)
        let result = null

        try {
            const res = await dispatch(updateConfigByKey(KEY_ENABLED, value, false))
            if (res?.status === 'success') {
                // message.success('Two-factor global enabled updated')
                setEnabled(Boolean(value))
                result = { success: true }

            } else {
                message.error('Failed to update setting "Enable TFA Globally"')
            }
        } catch (e) {
            message.error(e?.message || 'Failed to update setting "Enable TFA Globally"')
        } finally {
            setSavingEnabled(false)
        }
        return result
    }

    const savePref = async (value) => {
        setSavingPref(true)
        let result = null

        try {
            const res = await dispatch(updateConfigByKey(KEY_PREFERENCE, value, false))
            if (res?.status === 'success') {
                // message.success('Global two-factor preference updated')
                setPreference(value)
                result = { success: true }

            } else {
                message.error('Failed to update setting "Global TFA Preference"')
            }
        } catch (e) {
            message.error(e?.message || 'Failed to update setting "Global TFA Preference"')
        } finally {
            setSavingPref(false)
        }
        return result
    }

    async function handleSave() {
        if (savingEnabled || savingExpiry || savingPref) {
            return
        }
        dispatch(setLoading(true))

        const results = await Promise.all([
            saveEnabled(enabled),
            saveExpiry(expiry),
            savePref(preference),
        ])
        dispatch(setLoading(false))

        const allSucceeded = results.every(r => r?.success === true)

        if (allSucceeded) {
            openNotificationWithIcon("success",
                "Success!",
                "TFA Global Settings Updated Successfully")
            onClose()
        }
        // show success popup if all succeed

    }

    return (
        <Modal
            title="Two-Factor Authentication Settings"
            visible={visible}
            onCancel={onClose}
            footer={null}
            destroyOnClose
            centered
        >
            <Space direction="vertical" size="large" style={{ width: '100%' }}>

                {/* Global enable switch */}
                <Row align="middle" justify="space-between">
                    <Col>
                        {loadingEnabled ? (
                            <Spinner />
                        ) : (
                            <>
                                <Typography.Text style={{ fontFamily: 'tradeGothic', fontSize: '1rem', fontWeight: 700, marginBottom: 6, display: 'block' }}>Enable TFA Globally</Typography.Text>
                                <Switch
                                    checked={enabled}
                                    onChange={(checked) => {
                                        // optimistic toggle in UI
                                        setEnabled(checked)
                                        // saveEnabled(checked)
                                    }}
                                    checkedChildren="ON"
                                    unCheckedChildren="OFF"
                                />
                            </>
                        )}
                    </Col>
                </Row>


                {/* TOTP Expiry */}
                <Row align="middle" justify="space-between">
                    <Col>
                        {loadingExpiry ? (
                            <Spinner />
                        ) : (
                            <div>
                                <Typography.Text style={{ fontFamily: 'tradeGothic', fontSize: '1rem', fontWeight: 700, marginBottom: 6, display: 'block' }}>OTP Expiry Time</Typography.Text>
                                <InputNumber
                                    variant="underlined"
                                    min={1}
                                    value={expiry}
                                    onChange={(v) => setExpiry(v)}
                                    onBlur={() => {
                                        // send only when value is numeric
                                        // if (expiry !== null && expiry !== undefined) saveExpiry(expiry)
                                    }}
                                    placeholder="Minutes"
                                    style={{
                                        maxWidth: 200, width: '100%',
                                        padding: '6px 10px', border: 0, borderBottom: '1px solid #d9d9d9', background: 'rgb(244, 244, 244)'
                                    }}
                                />
                            </div>
                        )}
                    </Col>
                </Row>

                {/* Global preference radio */}
                <Row align="middle" justify="space-between">
                    <Col>
                        {loadingPref ? (
                            <Spinner />
                        ) : (
                            <>
                                <Typography.Text style={{ fontFamily: 'tradeGothic', fontSize: '1rem', fontWeight: 700, marginBottom: 6, display: 'block' }}>Global TFA Preference</Typography.Text>
                                <Space align="center">
                                    <Radio.Group
                                        value={preference}
                                        onChange={(e) => {
                                            const val = e.target.value
                                            setPreference(val)
                                            // savePref(val)
                                        }}
                                    >
                                        <Radio value={"Email"}>Email</Radio>
                                        <Radio value={"Phone"}>Phone</Radio>
                                    </Radio.Group>
                                </Space>
                            </>
                        )}
                    </Col>
                </Row>
            </Space>

            {
                !loadingEnabled && !loadingExpiry && !loadingPref && <div style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    marginTop: 16,
                    borderTop: "1px solid #d1d5db",
                    paddingTop: 16
                }}>
                    <AntdButton type='text' onClick={onClose}>Cancel</AntdButton>
                    <Button rounded loading={savingEnabled || savingExpiry || savingPref} onClick={handleSave}> Save</Button>
                </div>
            }

        </Modal>
    )
}